import React, { FC, ReactNode, useMemo } from 'react';
import {
  Box,
  Button,
  Center,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text
} from '@chakra-ui/react';
import Image from 'next/image';

import { SubmitButton } from '@app/components/SubmitButton';
import { MerklUserRewards } from '@app/hooks/merkl/useMerklUserRewards';
import { TbCoins } from 'react-icons/tb';
import { ExpandableDetails } from '@app/screens/positions/components/ExpandableDetails';
import { TokenIcon } from '@app/components/TokenIcon';
import { formatUnits } from 'ethers/lib/utils';
import { useHarvestMerkleRewards } from '@app/hooks/merkl/useHarvestMerkleRewards';
import { useMerklCampaigns } from '@app/hooks/merkl/useMerklCampaigns';
import { TokensPair } from '@app/components/TokensPair';
import { toOptionalFixed } from '@app/helpers/format';
import { PoolRewards } from '@app/screens/positions/components/PoolRewards';
import { NetworkIcon } from '@app/components/NetworkIcon';
import { getNetworkIconUrl } from '@app/screens/positions/components/MarketMakerWidget/helpers';
import uniq from 'lodash/uniq';
import { groupBy } from 'lodash';
import { useChainId, useSwitchChain } from '@thirdweb-dev/react';
import { MetromUserReward } from '@app/hooks/metrom/useMetromUserRewards';
import MetromLogo from 'src/assets/images/logo/metrom.svg';
import { useHarvestMetromRewards } from '@app/hooks/metrom/useHarvestMetromRewards';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  merklData: MerklUserRewards | null | undefined;
  metromData: MetromUserReward[] | null | undefined;
  totalValue: number;
}

export const MarketMakerWidgetModal: FC<Props> = ({
  isOpen,
  onClose,
  merklData,
  metromData,
  totalValue
}) => {
  const chainId = useChainId();
  const switchChain = useSwitchChain();

  const rewardsChainIds = useMemo(() => {
    if (!merklData) {
      return [];
    }

    return uniq(
      Object.values(merklData)
        .filter(item => +item.unclaimed > 0)
        .map(item => item.chainId)
    );
  }, [merklData]);

  const metromRewardsChainIds = useMemo(() => {
    if (!metromData) {
      return [];
    }

    return uniq(metromData.map(m => m.chainId));
  }, [metromData]);

  const { harvestRewards, isLoading: isHarvesting } =
    useHarvestMerkleRewards(rewardsChainIds);

  const {
    harvestRewards: harvestMetromRewards,
    isLoading: isHarvestingMetrom
  } = useHarvestMetromRewards(metromData);

  const { data: campaigns } = useMerklCampaigns();

  const poolsRows = useMemo(() => {
    if (!campaigns) {
      return [];
    }

    const _campaigns = groupBy(
      campaigns,
      c => c.campaignParameters.poolAddress
    );

    return Object.keys(_campaigns).map(key => {
      const c = _campaigns[key];

      const { campaignParameters } = c[0];
      const chainIds = c.map(item => item.chainId);

      return {
        key,
        label: (
          <HStack sx={{ display: 'flex', alignItems: 'center' }}>
            <TokensPair
              token0Symbol={campaignParameters.symbolToken0}
              token1Symbol={campaignParameters.symbolToken1}
            />{' '}
            <Text color="neutral.100" fontSize={14}>
              {campaignParameters.symbolToken0}-
              {campaignParameters.symbolToken1}
            </Text>
          </HStack>
        ),
        value: <PoolRewards key={key} pool={key} chainIds={chainIds} />
      };
    });
  }, [campaigns]);

  const { rewardsRows, hasClaimable } = useMemo(() => {
    if (!merklData) {
      return {
        rewardsRows: [],
        hasClaimable: false
      };
    }

    const hasMultipleNetworks =
      uniq(Object.values(merklData).map(k => k.chainId)).length > 1;

    let hasClaimable = false;
    const rows = Object.keys(merklData).reduce<
      { key: string; label: ReactNode; value: ReactNode }[]
    >((res, key) => {
      const d = merklData[key];

      if (d.symbol.toLowerCase() === 'aglamerkl') {
        return res;
      }

      if (Number(d.unclaimed) > 0) {
        hasClaimable = true;
      }

      res.push({
        key: key,
        label: (
          <Text fontSize={14} color="neutral.100">
            {d.symbol}
          </Text>
        ),
        value: (
          <HStack
            gap={10}
            alignItems="center"
            justifyContent={'space-between'}
            w="40%"
          >
            {hasMultipleNetworks ? (
              <HStack gap={2} alignItems="center">
                <NetworkIcon
                  iconUrl={getNetworkIconUrl(d.chainId).replace(
                    'ipfs://',
                    'https://ipfs.io/ipfs/'
                  )}
                />
              </HStack>
            ) : (
              <div>&nbsp;</div>
            )}
            <HStack gap={2} alignItems="center">
              <Text fontSize={14} color="neutral.100">
                {d.unclaimed
                  ? toOptionalFixed(
                      Number(formatUnits(d.unclaimed, d.decimals)),
                      3
                    )
                  : '-'}
              </Text>
              <TokenIcon symbol={d.symbol} />
            </HStack>
          </HStack>
        )
      });

      return res;
    }, []);

    return {
      hasClaimable,
      rewardsRows: rows
    };
  }, [merklData]);

  const metromRows = useMemo(() => {
    return (
      metromData?.map((item, i) => {
        return {
          key: i.toString(),
          label: (
            <HStack sx={{ display: 'flex', alignItems: 'center' }}>
              <div>&nbsp;</div>
            </HStack>
          ),
          value: (
            <HStack
              gap={10}
              alignItems="center"
              justifyContent={'space-between'}
              w="40%"
            >
              <div>&nbsp;</div>
              <HStack gap={2} alignItems="center">
                <Text fontSize={14} color="neutral.100">
                  {item.amount
                    ? formatUnits(item.amount, item.token.decimals)
                    : '-'}
                </Text>
                <TokenIcon symbol={item.token.symbol} />
              </HStack>
            </HStack>
          )
        };
      }) ?? []
    );
  }, [metromData]);

  function renderControls() {
    const isDisconnected = !chainId;
    const isNetworkMismatch =
      !!chainId &&
      rewardsChainIds.length > 0 &&
      !rewardsChainIds.includes(chainId);
    const isNoRewards = rewardsChainIds.length === 0;

    const isMetromNetworkMismatch =
      !!chainId &&
      metromRewardsChainIds.length > 0 &&
      !metromRewardsChainIds.includes(chainId.toString());
    const isMetromNoRewards = metromRewardsChainIds.length === 0;

    return (
      <HStack
        width="100%"
        alignItems="center"
        gap={4}
        justifyContent="space-between"
        mt={6}
      >
        <Button
          variant="secondary"
          onClick={() => {
            onClose();
          }}
          w="100%"
        >
          Cancel
        </Button>
        {(rewardsRows.length > 0 || poolsRows.length > 0) && (
          <SubmitButton
            label={isNetworkMismatch ? 'Switch network' : 'Harvest from Merkl'}
            isLoading={isHarvesting}
            isDisabled={
              !rewardsRows ||
              rewardsRows.length === 0 ||
              !hasClaimable ||
              isDisconnected ||
              isNoRewards
            }
            onClick={async () => {
              if (isNetworkMismatch) {
                await switchChain(rewardsChainIds[0]);
              } else {
                await harvestRewards();
              }
            }}
            mt={0}
          />
        )}
        {metromRows.length > 0 && (
          <SubmitButton
            label={
              isMetromNetworkMismatch ? 'Switch network' : 'Harvest from Metrom'
            }
            isLoading={isHarvestingMetrom}
            isDisabled={
              !metromRows ||
              metromRows.length === 0 ||
              isDisconnected ||
              isMetromNoRewards
            }
            onClick={async () => {
              if (isNetworkMismatch) {
                await switchChain(Number(metromRewardsChainIds[0]));
              } else {
                await harvestMetromRewards();
              }
            }}
            mt={0}
          />
        )}
      </HStack>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <ModalHeader>
          <HStack justify="space-between" px={0} py={0} mb="16px">
            <Center
              sx={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: 'neutral.1000'
              }}
            >
              <TbCoins size="24" color="#eed202" />
            </Center>
            <ModalCloseButton />
          </HStack>
          <Text fontSize="20px" color="white" fontWeight="500">
            Harvest all{' '}
            <Box as="span" color="orange">
              Market Makers
            </Box>{' '}
            rewards
          </Text>
          <Text
            fontSize="14px"
            color="neutral.400"
            fontWeight="500"
            mt={3}
            mb={6}
          >
            Claim extra yield from your active V4 manual positions
          </Text>
        </ModalHeader>
        {(rewardsRows.length > 0 || poolsRows.length > 0) && (
          <>
            <HStack pt={4} pb={3}>
              <Image
                src="/assets/images/merkl.png"
                width={120}
                height={64}
                alt="Merkl logo"
              />
            </HStack>
            <ExpandableDetails
              rows={rewardsRows}
              label="Pending rewards"
              totalValue={totalValue}
              isExpanded={rewardsRows.length > 0}
            />
            <ExpandableDetails rows={poolsRows} label="Rewards by pool" />
          </>
        )}

        {metromRows.length > 0 && (
          <>
            <HStack pt={4} pb={3}>
              <MetromLogo height={16} />
            </HStack>
            <ExpandableDetails
              rows={metromRows}
              label="Pending rewards"
              isExpanded
            />
          </>
        )}
        {renderControls()}
      </ModalContent>
    </Modal>
  );
};
